import Box from '@mui/material/Box'
import { common } from '@mui/material/colors'
import { useTheme } from '@mui/material/styles'
import Tooltip from '@mui/material/Tooltip'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'

import { SERVICE_ACTION_TYPE_TO_SVG } from 'settings/constants/workOrder'

import CalendarDayIcon from './CalendarDayIcon'
import CalendarDayStatusBar from './CalendarDayStatusBar'
import {
  StyledPickersDay,
  columnContainerStyles,
  iconRowStyles,
  ICON_ROW_HEIGHT_4,
  ICON_ROW_HEIGHT_5,
  GRID_COLUMN_WIDTH,
  GRID_COLUMN_WIDTH_LAST,
} from './styles'
import { buildIconsArray, buildTooltipTitle } from './utils'

// Column 1 labels (5 rows at 20% each)
const COLUMN_1_LABELS = ['Payments', 'Invoices', 'Exceptions', 'DisposalTickets', 'Fees']
// Column 2 labels (3 rows)
const COLUMN_2_LABELS = ['WorkOrderStatus', 'DayNumber', 'WorkOrders']
// Column 3 labels (4 rows at 25% each)
const COLUMN_3_LABELS = ['RecurrenceType', 'ServiceAction', 'Routes', 'Notes']

const CalendarDay = ({
  day,
  pickersDayProps,
  heatmapValue,
  dateStr,
  selectedRange,
  dayData,
  workOrderCount = 0,
  onRequestServiceAction = null,
  recurringServiceAction = null,
  exceptionCount = 0,
  invoiceCount = 0,
  disposalTicketCount = 0,
  feeCount = 0,
  noteCount = 0,
  paymentCount = 0,
  routeData,
  workOrderStatuses = [],
  onDayClick,
}) => {
  const theme = useTheme()
  const dayObj = day.day
  const isValidDate = !Number.isNaN(dayObj.getTime())

  // Range selection state
  const rangeStart = selectedRange[0]
  const rangeEnd = selectedRange[1]
  const isInRange = isValidDate && rangeStart && rangeEnd && dayObj >= rangeStart && dayObj <= rangeEnd
  const isRangeStart = isValidDate && rangeStart && dayObj.getTime() === rangeStart.getTime()
  const isRangeEnd = isValidDate && rangeEnd && dayObj.getTime() === rangeEnd.getTime()

  // Work order data
  const hasWorkOrdersForDay = workOrderCount > 0

  // Heatmap and route data (only show when there are work orders)
  const recurringCount = hasWorkOrdersForDay ? dayData?.recurringCount || 0 : 0
  const requestCount = hasWorkOrdersForDay ? dayData?.requestCount || 0 : 0
  const hasRoute = hasWorkOrdersForDay ? routeData?.hasRoute || false : false

  // Status data
  const uniqueStatuses = [...new Set(workOrderStatuses)]
  const workOrderStatus = hasWorkOrdersForDay && uniqueStatuses.length > 0 ? uniqueStatuses[0] : null
  const hasMultipleStatusTypes = hasWorkOrdersForDay && uniqueStatuses.length > 1

  // On-request service action
  const onRequestServiceActionName = hasWorkOrdersForDay ? onRequestServiceAction : null
  const onRequestServiceActionIcon = onRequestServiceActionName
    ? SERVICE_ACTION_TYPE_TO_SVG[onRequestServiceActionName.toUpperCase()]
    : null

  // Recurring service action
  const recurringServiceActionName = hasWorkOrdersForDay ? recurringServiceAction : null
  const recurringServiceActionIcon = recurringServiceActionName
    ? SERVICE_ACTION_TYPE_TO_SVG[recurringServiceActionName.toUpperCase()]
    : null

  // Recurrence display
  const showRecurring = hasWorkOrdersForDay && recurringCount > 0
  const showOnRequest = hasWorkOrdersForDay && requestCount > 0
  const hasBothRecurrenceTypes = showRecurring && showOnRequest

  // Icon color based on selection state
  const iconColor = isRangeStart || isRangeEnd ? common.white : undefined

  // Build icons array
  const icons = useMemo(
    () =>
      buildIconsArray({
        workOrderStatus,
        workOrderCount,
        noteCount,
        showOnRequest,
        requestCount,
        exceptionCount,
        paymentCount,
        feeCount,
        invoiceCount,
        disposalTicketCount,
        showRecurring,
        recurringCount,
        hasRoute,
        routeData,
        onRequestServiceActionIcon,
        onRequestServiceActionName,
        recurringServiceActionIcon,
        recurringServiceActionName,
        iconColor,
        theme,
      }),
    [
      workOrderStatus,
      workOrderCount,
      noteCount,
      showOnRequest,
      requestCount,
      exceptionCount,
      paymentCount,
      feeCount,
      invoiceCount,
      disposalTicketCount,
      showRecurring,
      recurringCount,
      hasRoute,
      routeData,
      onRequestServiceActionIcon,
      onRequestServiceActionName,
      recurringServiceActionIcon,
      recurringServiceActionName,
      iconColor,
      theme,
    ]
  )

  // Create icon map for quick lookup
  const iconMap = useMemo(() => {
    const map = {}
    icons.forEach(icon => {
      map[icon.label] = icon
    })
    return map
  }, [icons])

  // Build tooltip title
  const tooltipTitle = useMemo(
    () =>
      buildTooltipTitle({
        dateStr,
        recurringCount,
        requestCount,
        workOrderCount,
        onRequestServiceActionName,
        recurringServiceActionName,
        exceptionCount,
        invoiceCount,
        disposalTicketCount,
        feeCount,
        paymentCount,
        noteCount,
        hasRoute,
        routeData,
      }),
    [
      dateStr,
      recurringCount,
      requestCount,
      workOrderCount,
      onRequestServiceActionName,
      recurringServiceActionName,
      exceptionCount,
      invoiceCount,
      disposalTicketCount,
      feeCount,
      paymentCount,
      noteCount,
      hasRoute,
      routeData,
    ]
  )

  // Early return for invalid dates (after all hooks are called)
  if (!isValidDate) {
    return null
  }

  const renderIconCell = (label, iconData, height) => {
    if (!iconData) return <Box key={label} sx={{ height }} />
    return (
      <Box key={`${dateStr}-${label}`} sx={iconRowStyles(height)}>
        <CalendarDayIcon iconData={iconData} isRangeEnd={isRangeEnd} isRangeStart={isRangeStart} label={label} />
      </Box>
    )
  }

  return (
    <Tooltip arrow placement="top" title={tooltipTitle}>
      <Box sx={{ position: 'relative', display: 'inline-flex', width: '100%', height: '100%' }}>
        <StyledPickersDay
          {...pickersDayProps}
          day={dayObj}
          hasMultipleStatusTypes={hasMultipleStatusTypes}
          heatmapValue={heatmapValue}
          isInRange={isInRange}
          isRangeEnd={isRangeEnd}
          isRangeStart={isRangeStart}
          sx={{ position: 'relative' }}
          workOrderStatus={workOrderStatus}
          onDaySelect={onDayClick}
        />

        {/* Column 1: 5 rows at 20% each */}
        <Box sx={columnContainerStyles(0)}>{COLUMN_1_LABELS.map(label => renderIconCell(label, iconMap[label], ICON_ROW_HEIGHT_5))}</Box>

        {/* Column 2: 3 rows */}
        <Box sx={columnContainerStyles(GRID_COLUMN_WIDTH)}>
          {COLUMN_2_LABELS.map(label => {
            if (label === 'DayNumber') {
              return <Box key={label} sx={{ flex: 1 }} />
            }
            const iconData = iconMap[label]
            if (!iconData) return <Box key={label} sx={{ flex: 1 }} />
            return (
              <Box key={`${dateStr}-${label}`} sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CalendarDayIcon iconData={iconData} isRangeEnd={isRangeEnd} isRangeStart={isRangeStart} label={label} />
              </Box>
            )
          })}
        </Box>

        {/* Column 3: 4 rows at 25% each */}
        <Box sx={columnContainerStyles('66.66%', GRID_COLUMN_WIDTH_LAST)}>
          {hasBothRecurrenceTypes ? (
            <>
              {renderIconCell('OnRequest', iconMap.OnRequest, ICON_ROW_HEIGHT_4)}
              {renderIconCell('OnRequestServiceAction', iconMap.OnRequestServiceAction, ICON_ROW_HEIGHT_4)}
              {renderIconCell('Recurring', iconMap.Recurring, ICON_ROW_HEIGHT_4)}
              {renderIconCell('RecurringServiceAction', iconMap.RecurringServiceAction, ICON_ROW_HEIGHT_4)}
            </>
          ) : (
            COLUMN_3_LABELS.map(label => {
              let iconData = iconMap[label]
              // Handle RecurrenceType - show either OnRequest or Recurring (OnRequest has priority)
              if (label === 'RecurrenceType') {
                iconData = iconMap.OnRequest || iconMap.Recurring
              }
              // Handle ServiceAction - show either OnRequestServiceAction or RecurringServiceAction
              if (label === 'ServiceAction') {
                iconData = iconMap.OnRequestServiceAction || iconMap.RecurringServiceAction
              }
              return renderIconCell(iconData?.label || label, iconData, ICON_ROW_HEIGHT_4)
            })
          )}
        </Box>

        {/* Status bar for multiple work order statuses */}
        <CalendarDayStatusBar dateStr={dateStr} workOrderStatuses={hasMultipleStatusTypes ? workOrderStatuses : null} />
      </Box>
    </Tooltip>
  )
}

CalendarDay.propTypes = {
  day: PropTypes.object.isRequired,
  pickersDayProps: PropTypes.object.isRequired,
  heatmapValue: PropTypes.number.isRequired,
  dateStr: PropTypes.string.isRequired,
  selectedRange: PropTypes.array.isRequired,
  dayData: PropTypes.object,
  workOrderCount: PropTypes.number,
  onRequestServiceAction: PropTypes.string,
  recurringServiceAction: PropTypes.string,
  exceptionCount: PropTypes.number,
  invoiceCount: PropTypes.number,
  disposalTicketCount: PropTypes.number,
  feeCount: PropTypes.number,
  noteCount: PropTypes.number,
  paymentCount: PropTypes.number,
  routeData: PropTypes.object,
  workOrderStatuses: PropTypes.array,
  onDayClick: PropTypes.func.isRequired,
}

export default CalendarDay
